import ReactDOM from 'react-dom';
import { Dispatch, SetStateAction, useEffect, useRef, useState, ReactNode } from 'react';
import './ConfirmPopup.css';

interface ConfirmPopupProps {
    /** 표시 여부 — controlled */
    isOpen: boolean;
    /** 백드롭/ESC 닫힘 또는 mount/unmount 후속 처리를 위한 setter */
    setIsOpen: Dispatch<SetStateAction<boolean>>;

    /** 제목 (호출 측에서 i18n 처리해 전달) */
    title: string;
    /** 본문 — 문자열 또는 ReactNode (입력/리스트 합성 여지) */
    message?: ReactNode;

    /** 우측 주 액션 (단일 버튼이면 이것만) */
    rightBtnText: string;
    onRight: () => void;

    /** 좌측 보조 액션 — 생략 시 단일 버튼 풀폭 */
    leftBtnText?: string;
    onLeft?: () => void;

    /** 우측이 위험 액션이면 강조색 */
    danger?: boolean;

    /** 백드롭 클릭으로 닫힘 허용 — 기본 false (차단형 confirm) */
    closeOnBackdrop?: boolean;
    /** ESC 로 닫힘 허용 — 기본 true */
    closeOnEsc?: boolean;

    /** 열림 시 자동 focus 위치 — 호출자가 케이스별로 결정. 기본 "right" */
    defaultFocus?: "left" | "right" | "none";
}

// 다중 모달이 중첩될 수 있어 body scroll lock 은 카운터로 관리한다.
//   마지막 닫힘에서만 overflow 복원.
let lockCount = 0;
const lockBody = () => {
    if (lockCount === 0) document.body.style.overflow = "hidden";
    lockCount++;
};
const unlockBody = () => {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = "";
};

export const ConfirmPopup = ({
    isOpen,
    setIsOpen,
    title,
    message,
    rightBtnText,
    onRight,
    leftBtnText,
    onLeft,
    danger = false,
    closeOnBackdrop = false,
    closeOnEsc = true,
    defaultFocus = "right",
}: ConfirmPopupProps) => {
    const portalRoot = document.getElementById("portal");
    const [isVisible, setIsVisible] = useState(false);

    const cardRef = useRef<HTMLDivElement>(null);
    const scrimRef = useRef<HTMLDivElement>(null);
    const leftBtnRef = useRef<HTMLButtonElement>(null);
    const rightBtnRef = useRef<HTMLButtonElement>(null);

    const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // 열림 애니메이션 — BottomSheet 와 동일하게 RAF 이중으로 paint 보장 후 transition 시작
    const animateOpen = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        // iOS WebView 키보드가 열린 상태에서 모달을 띄우면 viewport 어긋남 — 입력 포커스 해제
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                cardRef.current?.classList.add("open");
                scrimRef.current?.classList.add("open");
                if (defaultFocus === "right") rightBtnRef.current?.focus();
                else if (defaultFocus === "left") leftBtnRef.current?.focus();
            });
        });
    };

    // 닫기 애니메이션 — transition(200ms) 종료 후 unmount
    const animateClose = () => {
        cardRef.current?.classList.remove("open");
        scrimRef.current?.classList.remove("open");
        closeTimer.current = setTimeout(() => {
            setIsVisible(false);
        }, 200);
    };

    // isOpen ↔ isVisible 동기화 (BottomSheet 패턴)
    useEffect(() => {
        if (isOpen) {
            setIsVisible(true);
        } else if (isVisible) {
            animateClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // 마운트(isVisible=true) 후 애니메이션 시작 + body scroll lock
    useEffect(() => {
        if (!isVisible) return;
        lockBody();
        animateOpen();
        return () => unlockBody();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible]);

    // ESC 닫기
    useEffect(() => {
        if (!isVisible || !closeOnEsc) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsOpen(false);
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isVisible, closeOnEsc, setIsOpen]);

    // Focus trap — Tab/Shift+Tab 으로 카드 안에서만 순환
    useEffect(() => {
        if (!isVisible) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const focusables = [leftBtnRef.current, rightBtnRef.current].filter(Boolean) as HTMLElement[];
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [isVisible]);

    if (!portalRoot || !isVisible) return null;

    const titleId = "confirm-popup-title";
    const messageId = "confirm-popup-message";

    return ReactDOM.createPortal(
        <div className="confirm-popup-container">
            <div
                className="confirm-popup-scrim"
                ref={scrimRef}
                onClick={() => {
                    if (closeOnBackdrop) setIsOpen(false);
                }}
            />
            <div
                className="confirm-popup"
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={message ? messageId : undefined}
            >
                <span className="confirm-popup-title" id={titleId}>{title}</span>
                {message && (
                    <div className="confirm-popup-message" id={messageId}>{message}</div>
                )}
                <div className="confirm-popup-actions">
                    {leftBtnText && (
                        <button
                            ref={leftBtnRef}
                            type="button"
                            className="confirm-popup-btn left"
                            onClick={onLeft}
                        >
                            {leftBtnText}
                        </button>
                    )}
                    <button
                        ref={rightBtnRef}
                        type="button"
                        className={`confirm-popup-btn right${danger ? " danger" : ""}`}
                        onClick={onRight}
                    >
                        {rightBtnText}
                    </button>
                </div>
            </div>
        </div>,
        portalRoot
    );
};
